import * as React from 'react';
import {Overlay} from "@athena-portal/components";
import FalconHeader from "../components/navigation/FalconHeader";
import FalconFooter from "../components/navigation/FalconFooter";
import {ICustomerPortalService, PartyPropertyEnum, Policy, Quote, resourcesHelper, Types} from '@falcon/services';
import {lazyInject} from '@athena-portal/core';
import {isBeforeRebranding, setLocalStorage} from "../CommonFunctions";

interface IState {
    hasPolicy: boolean,
    hasActiveQuote: boolean,
    redirectUrl: string,
    quotes: Quote[],
    dataLoaded: boolean,
}


export class AllianzOnlinePage extends React.Component<{}, IState> {

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    constructor(props: {}) {
        super(props);

        this.state = {
            hasPolicy: false,
            hasActiveQuote: false,
            redirectUrl: '/dashboard',
            quotes: [],
            dataLoaded: false,
        }
    }

    public componentDidMount(): void {
        const {search} = document.location;
        const params = new URLSearchParams(search);

        const retrieveQuote = params.get("retrieveQuote") === "true";
        if (!retrieveQuote) {
            this.customerPortalService.getPolicies().subscribe((policies: Policy[]) => {
                this.customerPortalService.getQuotes().subscribe((quotes: Quote[]) => {
                    this.setState(() => {
                        let redirectUrl = this.getRedirectUrl();

                        return {
                            hasPolicy: policies?.length > 0,
                            hasActiveQuote: quotes?.length > 0,
                            redirectUrl,
                            quotes,
                            dataLoaded: true
                        };
                    });
                });
            });
        } else {
            const product = params.get("product");
            const page = params.get("page");
            const interactionId = params.get("interactionId");
            const redirectUrl = `/journey/show/product/${product}/process/nb/${page}/${interactionId}`;
            this.setState({dataLoaded: true, hasActiveQuote: true, redirectUrl});
        }
    }

    public render() {
        if (!this.state.dataLoaded) {
            return <Overlay show={true}/>;
        } else {
            return (
                <React.Fragment>
                    <FalconHeader/>
                    <div className="online-page">
                        <div className="confetti-background">
                            <div className="heading">
                                <h1 className="font-weight-light">Our new name <br/> is <b className="font-weight-bold">{resourcesHelper.PRODUCT}</b></h1>
                            </div>
                        </div>

                        <div className="page-content-container">
                            <div className="page-content">
                                <p className="page-content-heading font-weight-600">Here's what you need to know</p>
                                <div className="info-sections text-content">
                                    <div className="left-section online-list">
                                        <div className="tier-logos">
                                            <img src="/Allianz/img/annual/motor/tier-1-small.svg"
                                                 alt="Tier logo" className="tier-logo"/>
                                            <img src="/Allianz/img/annual/motor/tier-2-small.svg"
                                                 alt="Tier logo" className="tier-logo"/>
                                            <img src="/Allianz/img/annual/motor/tier-3-small.svg" alt="Tier logo"
                                                 className="tier-logo"/>
                                        </div>

                                        <p className="font-weight-regular">Our three cover levels have new names:</p>
                                        <ul>
                                            <li><b className="font-weight-bold">Allianz Essentials</b> is now <b
                                                className="font-weight-bold">{resourcesHelper.PRODUCT_TIER_1}</b></li>
                                            <li><b className="font-weight-bold">Allianz Silver</b> is now <b
                                                className="font-weight-bold">{resourcesHelper.PRODUCT_TIER_2}</b></li>
                                            <li><b className="font-weight-bold">Allianz</b> (our highest level) is
                                                now <b
                                                    className="font-weight-bold">{resourcesHelper.PRODUCT_TIER_3}</b></li>
                                        </ul>

                                        <div className="icon-thumb-up"></div>
                                        <div>
                                            <p><b className="font-weight-bold">What do I need to do?</b> You don't need
                                                to
                                                do anything – your cover and everything else stays the same.</p>
                                        </div>
                                    </div>

                                    <div className="right-section online-list">
                                        <div className="information-section">
                                            <div className="icon-product-help-information"></div>
                                            <p>Whilst our name is changing, everything stays the same and it’s the same
                                                great product and service as before provided by Allianz.</p>
                                        </div>

                                        <div className="icon-product-help-question"></div>
                                        <p>Have a question about the new name? Please read our <a
                                            href={`https://www.allianz.co.uk/insurance/faq-allianz-online.html`}
                                            target="_blank">frequently asked questions.</a></p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex flex-column align-items-center justify-content-center sticky-button">
                            <div className="fade-overlay"></div>
                            {this.state.hasPolicy && <div className="button-container">
                                <button type="button" className="btn btn-primary allianz-online-btn"
                                        onClick={this.updatePartyPropertyAndRedirect}>
                                    Continue to your account
                                </button>
                            </div>}

                            {(this.state.hasActiveQuote && !this.state.hasPolicy) && <div className="button-container">
                                <button type="button" className="btn btn-primary"
                                        onClick={this.updateLocalStorageAndRedirect}>
                                    Continue to your quote
                                </button>
                            </div>}
                        </div>

                    </div>
                    <FalconFooter/>
                </React.Fragment>
            );
        }
    }

    private readonly updateLocalStorageAndRedirect = () => {
        const quotes = this.state.quotes;
        const quotesBeforeRebranding = quotes.filter(quote =>
            quote.interaction?.createdOn && isBeforeRebranding(quote.interaction.createdOn));
        quotesBeforeRebranding.forEach((quote: Quote) => {
            setLocalStorage("allianzOnlineShown" + quote.quoteId, true);
        })
        this.redirect(this.state.redirectUrl);
    }

    private readonly updatePartyPropertyAndRedirect = () => {
        this.customerPortalService.updatePartyProperty(PartyPropertyEnum.ALLIANZ_ONLINE_SHOWN, "true")
            .subscribe(() => {
                this.redirect(this.state.redirectUrl);
            })
    }

    private readonly getRedirectUrl = () => {
        const {search} = document.location;
        const params = new URLSearchParams(search);

        const retrieveQuote = params.get("retrieveQuote") === "true";
        const product = params.get("product");
        const page = params.get("page");
        const interactionId = params.get("interactionId");
        const dashboard = params.get("dashboard") === "true";

        if (dashboard) {
            return '/dashboard';
        }


        if (retrieveQuote) {
            return `/journey/show/product/${product}/process/nb/${page}/${interactionId}`;
        }

        switch (product) {
            case 'MotorInsurance':
                return '/dashboard/annualQuotes';
            case 'HomeInsurance':
                return '/dashboard/HomeInsuranceQuotes';
            default:
                return '/dashboard';
        }
    }

    private readonly redirect = (redirectUrl: string) => {
        window.location.assign(redirectUrl)
    }
}
